import React, { useEffect, useState } from "react";

import { Col, Row } from "react-bootstrap";

import { IdCheckStatus, InternalUserInfoFragment, ParticipantTypes } from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { FieldFormatter } from "@components/detail_drawer/FieldFormatter";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { DetailCell } from "@components/resale_submission/DetailCell";
import { useAuthenticationContext } from "@context/authentication";
import { DateFormat, formatStringToDateFormat, toFriendlyText } from "@util/index";

import { InternalUserInfoForm } from "./InternalUserInfoForm";

interface NotesProps {
  userInfo: InternalUserInfoFragment;
  accountStatus?: string;
  participantTypes?: ParticipantTypes[];
}

const INTERNAL_USER_INFO_FORMATTERS: FieldFormatter<InternalUserInfoFragment>[] = [
  {
    title: "IPI number",
    format: (internalUserInfo: InternalUserInfoFragment) => internalUserInfo.ipi_basenumber || "-",
  },
  { title: "SAM ID", format: (internalUserInfo: InternalUserInfoFragment) => internalUserInfo.sam_id || "-" },
  {
    title: "ID verification status",
    format: (internalUserInfo: InternalUserInfoFragment) =>
      internalUserInfo.id_check_status || IdCheckStatus.INCOMPLETE,
  },
  {
    title: "ID verification date/time",
    format: (internalUserInfo: InternalUserInfoFragment) =>
      internalUserInfo.id_check_time
        ? formatStringToDateFormat(internalUserInfo.id_check_time, DateFormat.DD_MM_YYYY_SLASH)
        : "-",
  },
  {
    title: "Terms & conditions",
    format: (internalUserInfo: InternalUserInfoFragment) =>
      internalUserInfo.terms_accepted ? "Accepted" : "Not accepted",
  },
  {
    title: "Terms & conditions date/time",
    format: (internalUserInfo: InternalUserInfoFragment) =>
      internalUserInfo.terms_accepted_time
        ? formatStringToDateFormat(internalUserInfo.terms_accepted_time, DateFormat.DD_MM_YYYY_SLASH)
        : "-",
  },
  {
    title: "Red flag account",
    format: (internalUserInfo: InternalUserInfoFragment) => (internalUserInfo.account_restriction ? "Yes" : "No"),
  },
  {
    title: "Parent account",
    format: (internalUserInfo: InternalUserInfoFragment) => internalUserInfo.parent_account || "-",
  },
  {
    title: "Supporting documentation sighted",
    format: (internalUserInfo: InternalUserInfoFragment) => (internalUserInfo.supporting_doc_provided ? "Yes" : "No"),
  },
  {
    title: "Bank account hold",
    format: (internalUserInfo: InternalUserInfoFragment) =>
      internalUserInfo.finance_hold ? toFriendlyText(internalUserInfo.finance_hold) : "-",
  },
];

export const InternalUserInfoAccordion: React.FC<NotesProps> = ({ userInfo, accountStatus, participantTypes }) => {
  const { auth } = useAuthenticationContext();

  const [isEditResale, setIsEditResale] = useState(false);
  const [internalUserData, setInternalUserData] = useState(userInfo);
  const handleContentClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  useEffect(() => setInternalUserData(userInfo), [userInfo]);

  return (
    <>
      <CustomAccordion
        accordionKey={"InternalUse"}
        contrastOpenStyle={false}
        header={<h5 className="mb-0">Internal use</h5>}
        defaultActiveKey={"InternalUse"}
        customHeaderStyle="py-4"
        showBottomLine
        preventCloseOnContentClick
      >
        <Row onClick={handleContentClick}>
          {isEditResale ? (
            <InternalUserInfoForm
              userInfo={internalUserData}
              setIsEditResale={setIsEditResale}
              setInternalUserData={setInternalUserData}
              accountStatus={accountStatus}
              participantTypes={participantTypes}
            />
          ) : (
            <Col md={11}>
              <Row className="gap-4">
                {INTERNAL_USER_INFO_FORMATTERS.map(({ title, format }) => (
                  <Col md={5} className="d-flex flex-column" key={title}>
                    <DetailCell title={title} value={format(internalUserData)} />
                  </Col>
                ))}
              </Row>
            </Col>
          )}

          {!isEditResale && (
            <Col md={1} className="d-flex justify-content-end">
              <ProtectedComponent auth={auth}>
                <Col xs="auto">
                  <IconLink
                    to="#"
                    src={PenWriteEditIcon}
                    text="Edit"
                    className="me-2"
                    onClick={() => setIsEditResale(true)}
                  />
                </Col>
              </ProtectedComponent>
            </Col>
          )}
        </Row>
      </CustomAccordion>
    </>
  );
};
